import React from 'react';
import {
    List,
} from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import {
    AppPageForm, Input, onFieldChange, toasty,
    onReactSelectChanged,
} from '../common/forms/FormElements';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { SaveButton } from '../common/forms/SaveButton';
import { Block, BlockBorder } from '../common/Block';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { ExecutionHistory } from './common/ExecutionHistory';

class DuplicateTimesheetViewModel {
    timesheetId = '';
    selectedDate = '';
}

class DuplicateTimesheet extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            formValidated: false,
            loading: false,
            saving: false,
            data: new DuplicateTimesheetViewModel(),
            timesheetOptions: [],
            isValidated: false,
        };

        this.onChange = this.onChange.bind(this);
        this.onReactSelectChanged = onReactSelectChanged.bind(this);
    }

    onChange = onFieldChange;

    onSubmit = async () => {
        const { data } = { ...this.state };

        this.setState({ saving: true });

        try {
            await httpClient.post(ApiRoutes.adminTool.duplicateTimesheet(), {
                ...data,
                selectedDate: new Date(data.selectedDate),
            });

            this.setState({
                data: {
                    ...data,
                    timesheetId: '',
                    selectedDate: '',
                },
                isValidated: false,
            });

            toasty.success('Successfully duplicated timesheet');

            // Refresh the table after successful submission
            await this.props.refreshUsageHistory();
        } catch (err) {
            console.error(err);
            toasty.error('Error duplicating the timesheet. Please check the console.');
            await this.props.refreshUsageHistory();
        } finally {
            this.setState({ saving: false });
        }
    };

    loadTimesheetOptions = debounce((inputValue, callback) => {
        httpClient.js(ApiRoutes.typeAheads.timesheetNumberSearch(inputValue))
            .then((data) => {
                this.setState({ timesheetOptions: data });
                callback(data);
            });
    }, 250);

    render() {
        const {
            loading, saving, isValidated, data, timesheetOptions,
        } = this.state;

        if (loading) {
            return (
                <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CustomCircularProgress
                        color="secondary"
                        variant="indeterminate"
                        label={
                            this.props.match.params.id
                                ? 'Getting Tool History...'
                                : 'Loading form...'
                        }
                    />
                </div>
            );
        }

        if (!(this.context ?? {}).user) return null;

        return (
            <AppPageForm
                backUrl={AppNavPaths.AdminTool}
                backLabel="Back to Admin Tools"
                formShown={this.context.formIsOpen}
                formId="adminToolList"
                formName="adminToolList"
                formHeading="Duplicate Timesheet"
                formRef={this.formRef}
                formIsColumn
                saving={saving}
                isValidated={isValidated}
                onSubmit={this.onSubmit}
                setIsValidated={(value) => {
                    this.setState({ isValidated: value });
                }}
            >
                <Block padding="medium">
                    <div>
                        <ValidatedSelect
                            component={AsyncSelect}
                            loadOptions={this.loadTimesheetOptions}
                            placeholder="Type Timesheet Number Here"
                            label="Timesheet Number"
                            name="data.timesheetId"
                            id="timesheetNumber"
                            value={
                                (timesheetOptions ?? []).find((x) => x.value === data.timesheetId) ?? null
                            }
                            onChange={this.onReactSelectChanged}
                            cacheOptions
                            required
                            error="Timesheet number is required"
                        />

                        <Input
                            label="New Date"
                            id="selectedDate"
                            name="data.selectedDate"
                            value={data.selectedDate ?? ''}
                            onChange={this.onChange}
                            type="date"
                            required
                        />

                        <BlockBorder />

                        <div className="d-flex justify-content-center">
                            <SaveButton
                                saving={saving}
                                label="Submit"
                            />
                        </div>
                    </div>
                </Block>

                <ExecutionHistory usageHistory={this.props.usageHistory}>
                    {(record) => (
                        <>
                            <div>
                                <strong>From:</strong>
                                <List type="unstyled">
                                    {record.input.timesheetId && (
                                        <li>
                                            <strong>Timesheet ID:</strong>
                                            {' '}
                                            {record.input.timesheetId}
                                        </li>
                                    )}

                                    {record.input.timesheetNumber && (
                                        <li>
                                            <strong>Timesheet:</strong>
                                            {' '}
                                            {record.input.timesheetNumber}
                                        </li>
                                    )}

                                    {record.input.originalDate && (
                                        <li>
                                            <strong>Timesheet Date:</strong>
                                            {' '}
                                            {record.input.originalDate}
                                        </li>
                                    )}
                                </List>
                            </div>

                            <div>
                                <strong>To:</strong>
                                <List type="unstyled">
                                    {record.output.targetDate && (
                                        <li>
                                            <strong>New Timesheet Date:</strong>
                                            {' '}
                                            {record.output.targetDate}
                                        </li>
                                    )}

                                    {record.output.newTimesheetNumber && (
                                        <li>
                                            <strong>New Timesheet Number:</strong>
                                            {' '}
                                            {record.output.newTimesheetNumber}
                                        </li>
                                    )}
                                </List>
                            </div>
                        </>
                    )}
                </ExecutionHistory>
            </AppPageForm>
        );
    }
}

export default DuplicateTimesheet;
